import React, { useRef, useEffect, useState, useCallback } from 'react';
import './LissajousReactApp.css'; // Import the dedicated CSS file

// React component for the interactive Lissajous Plotter.
// This version is fully client-side and uses plain CSS for styling.
function LissajousReactApp() {
    const canvasRef = useRef(null); // Ref for the canvas element
    const canvasContainerRef = useRef(null); // Ref for the canvas's parent container

    // States for X-axis wave parameters
    const [xWaveType, setXWaveType] = useState('sine');
    const [xFrequency, setXFrequency] = useState(2);
    const [xAmplitude, setXAmplitude] = useState(0.9);
    const [xPhaseDeg, setXPhaseDeg] = useState(0.0);

    // States for Y-axis wave parameters
    const [yWaveType, setYWaveType] = useState('sine');
    const [yFrequency, setYFrequency] = useState(2);
    const [yAmplitude, setYAmplitude] = useState(0.9);
    const [yPhaseDeg, setYPhaseDeg] = useState(90.0); // 90 degrees for initial circles/ellipses

    // Constants for waveform generation
    const sampleRate = 2000; // Number of samples per second
    const duration = 2.0;    // Total duration of the waveform in seconds
    const numPoints = Math.floor(sampleRate * duration); // Total number of data points
    // Pre-calculate time points (equivalent to numpy.linspace)
    const timePoints = Array.from({ length: numPoints }, (_, i) => i * (duration / numPoints));

    // Function to generate waveform data based on type, frequency, amplitude, and phase shift.
    const generateWave = useCallback((waveType, frequency, amplitude, phaseDeg) => {
        const phaseRad = phaseDeg * (Math.PI / 180); // Convert degrees to radians

        const samples = timePoints.map(t => {
            const angularArg = 2 * Math.PI * frequency * t + phaseRad;
            let sample = 0;

            if (waveType === 'sine') {
                sample = amplitude * Math.sin(angularArg);
            } else if (waveType === 'square') {
                sample = amplitude * Math.sign(Math.sin(angularArg));
            } else if (waveType === 'sawtooth') {
                // Sawtooth wave logic: fmod(time * frequency + phase_fraction, 1.0) * 2 - 1
                // Math.fmod is simulated using % operator for positive results and wrapping.
                const phaseFraction = phaseRad / (2 * Math.PI);
                let value = (t * frequency + phaseFraction) % 1.0;
                // Ensure positive modulo for sawtooth if value becomes negative due to phase
                if (value < 0) value += 1.0; 
                sample = amplitude * (2 * value - 1);
            }
            return sample;
        });

        return samples;
    }, [timePoints]); // timePoints is a stable dependency

    // Function to draw the Lissajous figure on the canvas
    const drawLissajous = useCallback((xData, yData) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        ctx.clearRect(0, 0, width, height); // Clear the canvas
        ctx.beginPath(); // Start a new drawing path
        ctx.strokeStyle = '#00FFFF'; // Cyan color for the figure
        ctx.lineWidth = 2; // Line width

        // Define padding for the Lissajous shape within the canvas
        const paddingRatio = 0.15; // 15% padding on each side (total 30% reduction in display size)
        const effectiveWidth = width * (1 - 2 * paddingRatio);
        const effectiveHeight = height * (1 - 2 * paddingRatio);

        // Determine the maximum scale factor based on the smaller effective dimension
        // We divide by 2 because coordinates range from -1 to 1 (max amplitude) and
        // the effective width/height is for the full dimension, not half.
        const scaleFactor = Math.min(effectiveWidth, effectiveHeight) / 2;

        // Helper function to get canvas X coordinates
        // Val ranges from -xAmplitude to +xAmplitude. Scale it to effective drawing area.
        const getCanvasX = (val) => width / 2 + val * scaleFactor;
        // Helper function to get canvas Y coordinates
        // Val ranges from -yAmplitude to +yAmplitude. Scale it to effective drawing area.
        // Y-axis is inverted on canvas, so subtract from height/2.
        const getCanvasY = (val) => height / 2 - val * scaleFactor;

        // Move the starting point to the first sample
        ctx.moveTo(getCanvasX(xData[0]), getCanvasY(yData[0]));

        // Draw the line through all points
        for (let i = 1; i < xData.length; i++) {
            ctx.lineTo(getCanvasX(xData[i]), getCanvasY(yData[i]));
        }
        ctx.stroke(); // Make the drawn line visible
    }, []); 

    // Effect to generate and draw waveforms whenever parameters change
    useEffect(() => {
        const xData = generateWave(xWaveType, xFrequency, xAmplitude, xPhaseDeg);
        const yData = generateWave(yWaveType, yFrequency, yAmplitude, yPhaseDeg);
        drawLissajous(xData, yData);
    }, [
        xWaveType, xFrequency, xAmplitude, xPhaseDeg,
        yWaveType, yFrequency, yAmplitude, yPhaseDeg,
        generateWave, drawLissajous // Include memoized functions as dependencies
    ]);

    // Effect to handle canvas resizing and ensure it remains square
    useEffect(() => {
        const canvas = canvasRef.current;
        const container = canvasContainerRef.current;
        if (!canvas || !container) return;

        const resizeCanvas = () => {
            // Calculate the minimum dimension (width or height) of the container
            const size = Math.min(container.clientWidth, container.clientHeight);
            canvas.width = size;
            canvas.height = size;
            
            // Redraw with current parameters after resize
            const xData = generateWave(xWaveType, xFrequency, xAmplitude, xPhaseDeg);
            const yData = generateWave(yWaveType, yFrequency, yAmplitude, yPhaseDeg);
            drawLissajous(xData, yData);
        };

        resizeCanvas(); // Set initial dimensions
        window.addEventListener('resize', resizeCanvas); // Add resize event listener

        // Cleanup function: remove listener on component unmount
        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, [
        generateWave, drawLissajous, // Dependencies for functions
        xWaveType, xFrequency, xAmplitude, xPhaseDeg, // Dependencies for current parameters
        yWaveType, yFrequency, yAmplitude, yPhaseDeg
    ]);

    return (
        <div className="lissajous-app-container">
            <h1 className="app-title">
                Lissajous Plotter
            </h1>

            <div className="controls-grid-container">
                {/* Controls for X-axis wave (Horizontal) */}
                <div className="wave-controls-section">
                    <h2 className="section-title">X-Axis Wave</h2>
                    <div className="control-group">
                        <label htmlFor="xWaveType" className="control-label">Wave Type:</label>
                        <select
                            id="xWaveType"
                            className="wave-select"
                            value={xWaveType}
                            onChange={(e) => setXWaveType(e.target.value)}
                        >
                            <option value="sine">Sine</option>
                            <option value="square">Square Wave</option>
                            <option value="sawtooth">Sawtooth</option>
                        </select>
                    </div>
                    <div className="control-group">
                        <label htmlFor="xFrequency" className="control-label">Frequency (Ratio):</label>
                        <input
                            type="range"
                            id="xFrequency"
                            min="1" max="20" step="1"
                            value={xFrequency}
                            onChange={(e) => setXFrequency(parseInt(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{xFrequency}</span>
                    </div>
                    <div className="control-group">
                        <label htmlFor="xAmplitude" className="control-label">Amplitude:</label>
                        <input
                            type="range"
                            id="xAmplitude"
                            min="0.1" max="1.0" step="0.01"
                            value={xAmplitude}
                            onChange={(e) => setXAmplitude(parseFloat(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{xAmplitude.toFixed(2)}</span>
                    </div>
                    <div className="control-group">
                        <label htmlFor="xPhaseDeg" className="control-label">Phase (Degrees):</label>
                        <input
                            type="range"
                            id="xPhaseDeg"
                            min="-90" max="90" step="1"
                            value={xPhaseDeg}
                            onChange={(e) => setXPhaseDeg(parseFloat(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{xPhaseDeg.toFixed(1)}°</span>
                    </div>
                </div>

                {/* Controls for Y-axis wave (Vertical) */}
                <div className="wave-controls-section">
                    <h2 className="section-title">Y-Axis Wave</h2>
                    <div className="control-group">
                        <label htmlFor="yWaveType" className="control-label">Wave Type:</label>
                        <select
                            id="yWaveType"
                            className="wave-select"
                            value={yWaveType}
                            onChange={(e) => setYWaveType(e.target.value)}
                        >
                            <option value="sine">Sine</option>
                            <option value="square">Square Wave</option>
                            <option value="sawtooth">Sawtooth</option>
                        </select>
                    </div>
                    <div className="control-group">
                        <label htmlFor="yFrequency" className="control-label">Frequency (Ratio):</label>
                        <input
                            type="range"
                            id="yFrequency"
                            min="1" max="20" step="1"
                            value={yFrequency}
                            onChange={(e) => setYFrequency(parseInt(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{yFrequency}</span>
                    </div>
                    <div className="control-group">
                        <label htmlFor="yAmplitude" className="control-label">Amplitude:</label>
                        <input
                            type="range"
                            id="yAmplitude"
                            min="0.1" max="1.0" step="0.01"
                            value={yAmplitude}
                            onChange={(e) => setYAmplitude(parseFloat(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{yAmplitude.toFixed(2)}</span>
                    </div>
                    <div className="control-group">
                        <label htmlFor="yPhaseDeg" className="control-label">Phase (Degrees):</label>
                        <input
                            type="range"
                            id="yPhaseDeg"
                            min="-90" max="90" step="1"
                            value={yPhaseDeg}
                            onChange={(e) => setYPhaseDeg(parseFloat(e.target.value))}
                            className="range-input"
                        />
                        <span className="value-display">{yPhaseDeg.toFixed(1)}°</span>
                    </div>
                </div>
            </div>

            {/* Canvas Area for the Lissajous Plotter */}
            <div 
                ref={canvasContainerRef} 
                className="canvas-display-container" 
            >
                <canvas ref={canvasRef}></canvas>
            </div>
        </div>
    );
}

export default LissajousReactApp;
